import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from 'src/environments/environment';
import { NotificationEvent, NotificationEventTypeAction, SaleRequestNotificationDto, StudySubmittedNotificationDto } from '../models/common/notification-event.model';

@Injectable({ providedIn: 'root' })
export class SalesSignalrService implements OnDestroy {
  public baseUrl = environment.api2BaseUrl;
  private hubConnection!: signalR.HubConnection;
  private currentSaleId?: number;
  
  constructor(private http: HttpClient) { }


  async startConnection(token: string): Promise<boolean> {
    // If already connected, return true
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      return true;
    }

    // If hubConnection exists but is not connected, clear it for a clean retry
    if (this.hubConnection && this.hubConnection.state !== signalR.HubConnectionState.Connected) {
      this.hubConnection = undefined!;
    }

    let isRealtimeEnabled = false;
    try {
      // Use HttpClient so AuthInterceptor applies
      isRealtimeEnabled = await this.http
        .get<boolean>(`${this.baseUrl}/config/realtimecommunication`)
        .toPromise();
    } catch (err) {
      console.error('Failed to check realtime communication config', err);
      return false;
    }

    if (!isRealtimeEnabled) {
      return false;
    }

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.baseUrl}/sales-notification-hub`, {
        accessTokenFactory: () => token
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Critical)
      .build();

    try {
      await this.hubConnection.start();
      return true;
    } catch (err) {
      console.error('SignalR connection error', err);
      this.hubConnection = undefined!;
      this.currentSaleId = undefined;
      return false;
    }
  }


  isConnected(): boolean {
    return this.hubConnection?.state === signalR.HubConnectionState.Connected;
  }

  joinSale(saleId: number) {
    this.currentSaleId = saleId;

    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      this.hubConnection.invoke('JoinSale', saleId)
        .catch(err => console.error('JoinSale failed', err));
    }
  }

  onStudySubmitted(callback: (notification: NotificationEvent<StudySubmittedNotificationDto>) => void) {
      this.hubConnection.on(NotificationEventTypeAction.StudySubmitted, callback);
  }

  onSaleRequested(callback: (notification: NotificationEvent<SaleRequestNotificationDto>) => void) {
      this.hubConnection.on(NotificationEventTypeAction.SaleAccess, callback);
  }
  onSaleResult(callback: (notification: NotificationEvent<any>) => void) {
      this.hubConnection.on(NotificationEventTypeAction.SaleResult, callback);
  }
 

  forceCloseConnection() {
    if (this.hubConnection) {
      const connection = this.hubConnection;
      this.hubConnection = undefined!;
      this.currentSaleId = undefined;

      connection.stop()
        .then(() => {
          console.log('SignalR connection closed');
        })
        .catch(err => {
          console.error('Failed to stop SignalR connection', err);
        });
    }
  }


  ngOnDestroy(): void {
    this.forceCloseConnection();
  }
}
